import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { verificationService } from '../../../services/verificationService';

const VerificationDetailModal = ({ request, onClose, onApprove, onReject, onMarkUnderReview }) => {
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [realtimeChannel, setRealtimeChannel] = useState(null);

  useEffect(() => {
    loadHistory();

    // Subscribe to history updates
    const channel = verificationService?.subscribeToHistory(request?.id, (newHistoryItem) => {
      setHistory(prev => [newHistoryItem, ...prev]);
    });

    setRealtimeChannel(channel);

    return () => {
      if (channel) {
        verificationService?.unsubscribe(channel);
      }
    };
  }, [request?.id]);

  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      const historyData = await verificationService?.getVerificationHistory(request?.id);
      setHistory(historyData);
    } catch (err) {
      console.error('Error loading history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason?.trim()) return;
    await onReject(request, rejectionReason);
    setShowRejectForm(false);
    setRejectionReason('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: 'Clock',
      under_review: 'Eye',
      approved: 'CheckCircle',
      rejected: 'XCircle'
    };
    return icons?.[status] || 'Circle';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-warning',
      under_review: 'text-primary',
      approved: 'text-success',
      rejected: 'text-error'
    };
    return colors?.[status] || 'text-muted-foreground';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-2xl elevation-lg border border-border max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-1">Verification Details</h2>
            <p className="text-sm text-muted-foreground">{request?.email}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-xl transition-colors"
          >
            <Icon name="X" size={24} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* User Information */}
          <div className="bg-muted/50 rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Icon name="User" size={20} className="text-primary" />
              User Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Full Name</p>
                <p className="text-sm font-medium text-foreground">{request?.fullName}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Email</p>
                <p className="text-sm font-medium text-foreground">{request?.email}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Role</p>
                <p className="text-sm font-medium text-foreground capitalize">{request?.role}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Status</p>
                <p className="text-sm font-medium text-foreground capitalize">{request?.status?.replace('_', ' ')}</p>
              </div>
              {request?.industry && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Industry</p>
                  <p className="text-sm font-medium text-foreground">{request?.industry}</p>
                </div>
              )}
              {request?.location && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Location</p>
                  <p className="text-sm font-medium text-foreground">{request?.location}</p>
                </div>
              )}
              {request?.experience && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Experience</p>
                  <p className="text-sm font-medium text-foreground">{request?.experience} years</p>
                </div>
              )}
              {request?.seniority && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Seniority</p>
                  <p className="text-sm font-medium text-foreground">{request?.seniority}</p>
                </div>
              )}
            </div>
          </div>

          {/* Company Information (for employers) */}
          {request?.role === 'employer' && (request?.companyName || request?.companyWebsite) && (
            <div className="bg-muted/50 rounded-xl p-6 space-y-4">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Icon name="Building2" size={20} className="text-primary" />
                Company Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {request?.companyName && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Company Name</p>
                    <p className="text-sm font-medium text-foreground">{request?.companyName}</p>
                  </div>
                )}
                {request?.companyWebsite && (
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Website</p>
                    <a 
                      href={request?.companyWebsite} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-primary hover:underline"
                    >
                      {request?.companyWebsite}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Bio */}
          {request?.bio && (
            <div className="bg-muted/50 rounded-xl p-6 space-y-3">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Icon name="FileText" size={20} className="text-primary" />
                Professional Bio
              </h3>
              <p className="text-sm text-foreground whitespace-pre-wrap">{request?.bio}</p>
            </div>
          )}

          {/* Verification Metrics */}
          <div className="bg-muted/50 rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Icon name="BarChart3" size={20} className="text-primary" />
              Verification Metrics
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-background rounded-xl p-4 text-center">
                <p className="text-xs text-muted-foreground mb-2">Qualification Score</p>
                <p className="text-2xl font-bold text-foreground">{request?.qualificationScore || 0}</p>
              </div>
              <div className="bg-background rounded-xl p-4 text-center">
                <p className="text-xs text-muted-foreground mb-2">Verification Score</p>
                <p className="text-2xl font-bold text-foreground">{request?.verificationScore || 0}</p>
              </div>
              <div className="bg-background rounded-xl p-4 text-center">
                <p className="text-xs text-muted-foreground mb-2">Risk Level</p>
                <p className="text-2xl font-bold text-foreground capitalize">{request?.riskLevel || 'low'}</p>
              </div>
            </div>
          </div>

          {/* Verification History */}
          <div className="bg-muted/50 rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Icon name="History" size={20} className="text-primary" />
              Verification History
            </h3>
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <Icon name="Loader" size={32} className="animate-spin text-primary" />
              </div>
            ) : history?.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No history available</p>
            ) : (
              <div className="space-y-3">
                {history?.map((item) => (
                  <div key={item?.id} className="bg-background rounded-xl p-4 flex items-start gap-4">
                    <div className={`p-2 rounded-lg bg-muted ${getStatusColor(item?.newStatus)}`}>
                      <Icon name={getStatusIcon(item?.newStatus)} size={20} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-sm font-medium text-foreground">
                          Status changed to <span className="capitalize">{item?.newStatus?.replace('_', ' ')}</span>
                        </p>
                        <p className="text-xs text-muted-foreground">{formatDate(item?.createdAt)}</p>
                      </div>
                      <p className="text-xs text-muted-foreground mb-1">By: {item?.changedByName}</p>
                      {item?.notes && (
                        <p className="text-sm text-foreground mt-2">{item?.notes}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Links */}
          {request?.linkedinUrl && (
            <div className="bg-muted/50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-foreground flex items-center gap-2 mb-3">
                <Icon name="Link" size={20} className="text-primary" />
                External Links
              </h3>
              <a 
                href={request?.linkedinUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-primary hover:underline"
              >
                <Icon name="Linkedin" size={16} />
                <span className="text-sm">LinkedIn Profile</span>
              </a>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="border-t border-border p-6">
          {showRejectForm ? (
            <div className="space-y-4">
              <textarea
                className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                rows={3}
                placeholder="Enter rejection reason..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e?.target?.value)}
              />
              <div className="flex gap-3">
                <Button
                  variant="destructive"
                  onClick={handleReject}
                  disabled={!rejectionReason?.trim()}
                  className="flex-1"
                >
                  Confirm Rejection
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowRejectForm(false);
                    setRejectionReason('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              {request?.status === 'pending' && (
                <>
                  <Button
                    variant="outline"
                    iconName="Eye"
                    iconPosition="left"
                    onClick={() => {
                      onMarkUnderReview(request);
                      onClose();
                    }}
                    className="flex-1"
                  >
                    Mark Under Review
                  </Button>
                  <Button
                    variant="success"
                    iconName="Check"
                    iconPosition="left"
                    onClick={() => {
                      onApprove(request);
                      onClose();
                    }}
                    className="flex-1"
                  >
                    Approve Request
                  </Button>
                  <Button
                    variant="destructive"
                    iconName="X"
                    iconPosition="left"
                    onClick={() => setShowRejectForm(true)}
                    className="flex-1"
                  >
                    Reject Request
                  </Button>
                </>
              )}
              {request?.status === 'under_review' && (
                <>
                  <Button
                    variant="success"
                    iconName="Check"
                    iconPosition="left"
                    onClick={() => {
                      onApprove(request);
                      onClose();
                    }}
                    className="flex-1"
                  >
                    Approve Request
                  </Button>
                  <Button
                    variant="destructive"
                    iconName="X"
                    iconPosition="left"
                    onClick={() => setShowRejectForm(true)}
                    className="flex-1"
                  >
                    Reject Request
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Close
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerificationDetailModal;